import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { X, Gift, Check, ArrowRight, ArrowLeft } from "lucide-react";
import { Button } from "./ui/button";
import { BACKEND_BASE_URL } from "../config/api.config";
import { trackEvent } from "../utils/centerfyTracking";
import { useFormTracking } from "../hooks/useFormTracking";
import { trackModalOpen, trackModalClose } from "../utils/analytics";

// Initialize Stripe
let stripePromise = null;
const getStripe = async () => {
  if (!stripePromise) {
    const response = await fetch(
      `${BACKEND_BASE_URL}/api/payment/publishable-key`,
    );
    const { publishableKey } = await response.json();
    stripePromise = loadStripe(publishableKey);
  }
  return stripePromise;
};

/**
 * Multi-Step Holiday Gift Card Popup
 *
 * STEP 1: Offer ($89 for $120) - Buy for Myself / Buy for Someone Else
 * STEP 2: Contact Information
 * STEP 3: Payment
 */
export default function HolidayGiftCardMultiStepPopup({
  isOpen,
  onClose,
  onSuccess,
  variant = "a",
}) {
  const [step, setStep] = useState(1); // 1 = offer, 2 = contact, 3 = payment
  const [isForSomeoneElse, setIsForSomeoneElse] = useState(false);
  const [stripePromiseState, setStripePromiseState] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    recipientName: "",
    recipientEmail: "",
  });

  const [errors, setErrors] = useState({});
  const [modalOpenTime, setModalOpenTime] = useState(null);

  // Form tracking
  const {
    handleFieldFocus,
    handleFieldBlur,
    handleFieldChange,
    handleSubmit: trackFormSubmit,
    handleError: trackFormError,
  } = useFormTracking('holiday_gift_card_form', 'holiday-gift-card-form');

  // Handle visibility and body scroll + modal tracking
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      document.body.style.overflow = "hidden";
      // Track modal open
      const openTime = Date.now();
      setModalOpenTime(openTime);
      trackModalOpen('holiday_gift_card_popup', 'holiday-gift-card-popup', 'exit_intent');
    } else {
      document.body.style.overflow = "unset";
      // Track modal close
      if (modalOpenTime) {
        const timeOpen = Math.round((Date.now() - modalOpenTime) / 1000);
        trackModalClose('holiday_gift_card_popup', timeOpen);
        setModalOpenTime(null);
      }
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen, modalOpenTime]);

  // Reset to step 1 when popup closes
  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setIsForSomeoneElse(false);
      setFormData({
        fullName: "",
        email: "",
        phone: "",
        recipientName: "",
        recipientEmail: "",
      });
      setErrors({});
      setClientSecret(null);
    }
  }, [isOpen]);

  // Load Stripe on mount
  useEffect(() => {
    getStripe().then(setStripePromiseState);
  }, []);

  // Create payment intent when moving to step 3
  useEffect(() => {
    if (step === 3 && !clientSecret && formData.email) {
      createPaymentIntent();
    }
  }, [step, formData.email]);

  const createPaymentIntent = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${BACKEND_BASE_URL}/api/gift-cards/holiday/payment-intent`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: formData.email,
            amount: 89,
          }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to create payment intent");
      }

      const data = await response.json();
      setClientSecret(data.clientSecret);
    } catch (error) {
      console.error("Error creating payment intent:", error);
      setErrors({ payment: error.message });
    } finally {
      setLoading(false);
    }
  };

  const validateStep2 = () => {
    const newErrors = {};

    // Sender information (always required)
    if (!formData.fullName.trim()) {
      newErrors.fullName = "Your full name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Your email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Recipient information (required if buying for someone else)
    if (isForSomeoneElse) {
      if (!formData.recipientName.trim()) {
        newErrors.recipientName = "Recipient name is required";
      }

      if (!formData.recipientEmail.trim()) {
        newErrors.recipientEmail = "Recipient email is required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.recipientEmail)) {
        newErrors.recipientEmail = "Please enter a valid email address";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleStep1Continue = (forSomeoneElse) => {
    setIsForSomeoneElse(forSomeoneElse);
    setStep(2);

    // Track selection
    trackEvent("click", {
      elementId: forSomeoneElse
        ? "exit-popup-claim-for-other"
        : "exit-popup-claim-cta",
      variant,
      pagePath: "/gift-cards/christmas",
    });
  };

  const handleStep2Submit = async (e) => {
    e.preventDefault();

    if (!validateStep2()) {
      // Track validation errors
      Object.keys(errors).forEach((fieldName) => {
        if (errors[fieldName]) {
          trackFormError(fieldName, 'validation_error', errors[fieldName]);
        }
      });
      trackFormSubmit(false);
      return;
    }

    // Track form submission
    await trackEvent("formsubmit", {
      formId: "holiday-gift-card-step1",
      formData: {
        fullName: formData.fullName,
        email: formData.email,
        hasPhone: !!formData.phone,
      },
    });
    
    // Track successful form submit
    trackFormSubmit(true);

    // Submit to GHL to tag as "giftcard-lead"
    try {
      await fetch(`${BACKEND_BASE_URL}/api/gift-cards/holiday/submit-lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fullName: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          variant,
          recipientName: isForSomeoneElse ? formData.recipientName : undefined,
          recipientEmail: isForSomeoneElse
            ? formData.recipientEmail
            : undefined,
        }),
      });
    } catch (error) {
      console.error("Error submitting lead:", error);
    }

    setStep(3);
  };

  const handleBack = () => {
    if (step === 3) {
      setStep(2);
      setClientSecret(null);
    } else if (step === 2) {
      setStep(1);
    }
  };

  const handleClose = () => {
    setStep(1);
    setIsForSomeoneElse(false);
    setFormData({
      fullName: "",
      email: "",
      phone: "",
      recipientName: "",
      recipientEmail: "",
    });
    setErrors({});
    setClientSecret(null);
    onClose();
  };

  if (!isVisible) return null;

  // Variant A: Original design
  const variantAContent = (
    <div className="text-center">
      <div className="text-6xl mb-4">🎁</div>
      <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
        Don't Miss Your $120 Massage Gift Card!
      </h2>
      <p className="text-lg md:text-xl text-gray-600 mb-6">
        Today only: Pay $89 and lock your holiday deal.
      </p>
      <div className="flex items-center justify-center gap-2 mb-6">
        <span className="text-2xl text-gray-400 line-through">$120</span>
        <span className="text-4xl font-bold text-green-600">$89</span>
        <span className="text-lg text-gray-600">one-time</span>
      </div>
      <p className="text-sm text-gray-500 mb-8">
        Save $31 - Limited time holiday offer
      </p>
    </div>
  );

  // Variant B: Simplified design
  const variantBContent = (
    <div className="text-center">
      <div className="text-6xl mb-4">🎁</div>
      <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
        Get $120 Massage Gift Card for Just $89
      </h2>
      <p className="text-lg md:text-xl text-gray-600 mb-8">
        Perfect holiday gift. Limited time offer.
      </p>
      <div className="bg-green-50 border-2 border-green-500 rounded-lg p-4 mb-6">
        <div className="text-3xl font-bold text-green-600">$89</div>
        <div className="text-sm text-gray-600">Get $120 value</div>
      </div>
    </div>
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            onClick={handleClose}
          />

          {/* Popup Container */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.3, type: "spring", stiffness: 300 }}
              className="w-full max-w-lg bg-white rounded-2xl shadow-2xl p-6 md:p-8 overflow-y-auto max-h-[90vh] pointer-events-auto"
              onClick={(e) => e.stopPropagation()}>
              {/* Close Button */}
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors">
                <X className="w-6 h-6" />
              </button>

              {/* Progress Indicator (for steps 2 and 3) */}
              {(step === 2 || step === 3) && (
                <div className="flex items-center justify-center mb-6">
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                        step >= 2
                          ? "bg-green-600 text-white"
                          : "bg-gray-200 text-gray-500"
                      }`}>
                      {step > 2 ? <Check className="w-5 h-5" /> : "1"}
                    </div>
                    <div
                      className={`w-16 h-1 ${
                        step >= 3 ? "bg-green-600" : "bg-gray-200"
                      }`}
                    />
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                        step >= 3
                          ? "bg-green-600 text-white"
                          : "bg-gray-200 text-gray-500"
                      }`}>
                      {step > 3 ? <Check className="w-5 h-5" /> : "2"}
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 1: Offer */}
              {step === 1 && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}>
                  {variant === "a" ? variantAContent : variantBContent}

                  {/* CTA Buttons */}
                  <div className="space-y-3">
                    <Button
                      onClick={() => handleStep1Continue(false)}
                      size="lg"
                      className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white text-lg py-6 h-auto shadow-lg hover:shadow-xl transition-all duration-300">
                      <Gift className="w-5 h-5 mr-2" />
                      Buy for Myself
                    </Button>
                    <Button
                      onClick={() => handleStep1Continue(true)}
                      size="lg"
                      variant="outline"
                      className="w-full border-2 border-green-600 text-green-600 hover:bg-green-50 text-lg py-6 h-auto shadow-md hover:shadow-lg transition-all duration-300">
                      <Gift className="w-5 h-5 mr-2" />
                      Buy for Someone Else
                    </Button>
                  </div>

                  {/* Trust indicators */}
                  <p className="text-xs text-center text-gray-500 mt-4">
                    Secure checkout • Email delivery • Valid for 1 year
                  </p>
                </motion.div>
              )}

              {/* STEP 2: Contact Information */}
              {step === 2 && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}>
                  <div className="text-center mb-6">
                    <Gift className="w-12 h-12 text-green-600 mx-auto mb-4" />
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                      {isForSomeoneElse
                        ? "Gift Someone Special"
                        : "Get Your $120 Gift Card"}
                    </h2>
                    <p className="text-gray-600">
                      Just $89 today - Limited time offer
                    </p>
                  </div>

                  <form onSubmit={handleStep2Submit} className="space-y-4">
                    {/* Your Information Section */}
                    <div className="border-b border-gray-200 pb-4 mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">
                        Your Information
                      </h3>

                      {/* Full Name */}
                      <div className="mb-4">
                        <label
                          htmlFor="fullName"
                          className="block text-sm font-medium text-gray-700 mb-2">
                          Your Full Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="fullName"
                          name="fullName"
                          value={formData.fullName}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              fullName: e.target.value,
                            });
                            handleFieldChange('fullName', e.target.value, 'text');
                          }}
                          onFocus={(e) => handleFieldFocus('fullName', 'text')}
                          onBlur={(e) => handleFieldBlur('fullName')}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                            errors.fullName
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                          placeholder="John Doe"
                        />
                        {errors.fullName && (
                          <p className="text-red-500 text-sm mt-1">
                            {errors.fullName}
                          </p>
                        )}
                      </div>

                      {/* Email */}
                      <div className="mb-4">
                        <label
                          htmlFor="email"
                          className="block text-sm font-medium text-gray-700 mb-2">
                          Your Email <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={(e) => {
                            setFormData({ ...formData, email: e.target.value });
                            handleFieldChange('email', e.target.value, 'email');
                          }}
                          onFocus={(e) => handleFieldFocus('email', 'email')}
                          onBlur={(e) => handleFieldBlur('email')}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                            errors.email ? "border-red-500" : "border-gray-300"
                          }`}
                          placeholder="john@example.com"
                        />
                        {errors.email && (
                          <p className="text-red-500 text-sm mt-1">
                            {errors.email}
                          </p>
                        )}
                      </div>

                      {/* Phone (Optional) */}
                      <div>
                        <label
                          htmlFor="phone"
                          className="block text-sm font-medium text-gray-700 mb-2">
                          Phone{" "}
                          <span className="text-gray-500">(Optional)</span>
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={(e) => {
                            setFormData({ ...formData, phone: e.target.value });
                            handleFieldChange('phone', e.target.value, 'tel');
                          }}
                          onFocus={(e) => handleFieldFocus('phone', 'tel')}
                          onBlur={(e) => handleFieldBlur('phone')}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          placeholder="(555) 123-4567"
                        />
                      </div>
                    </div>

                    {/* Recipient Information Section (only if buying for someone else) */}
                    {isForSomeoneElse && (
                      <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 mb-4">
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">
                          Recipient Information
                        </h3>

                        {/* Recipient Name */}
                        <div className="mb-4">
                          <label
                            htmlFor="recipientName"
                            className="block text-sm font-medium text-gray-700 mb-2">
                            Recipient Name{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            id="recipientName"
                            name="recipientName"
                            value={formData.recipientName}
                            onChange={(e) => {
                              setFormData({
                                ...formData,
                                recipientName: e.target.value,
                              });
                              handleFieldChange('recipientName', e.target.value, 'text');
                            }}
                            onFocus={(e) => handleFieldFocus('recipientName', 'text')}
                            onBlur={(e) => handleFieldBlur('recipientName')}
                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                              errors.recipientName
                                ? "border-red-500"
                                : "border-gray-300"
                            }`}
                            placeholder="Jane Smith"
                          />
                          {errors.recipientName && (
                            <p className="text-red-500 text-sm mt-1">
                              {errors.recipientName}
                            </p>
                          )}
                        </div>

                        {/* Recipient Email */}
                        <div>
                          <label
                            htmlFor="recipientEmail"
                            className="block text-sm font-medium text-gray-700 mb-2">
                            Recipient Email{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="email"
                            id="recipientEmail"
                            name="recipientEmail"
                            value={formData.recipientEmail}
                            onChange={(e) => {
                              setFormData({
                                ...formData,
                                recipientEmail: e.target.value,
                              });
                              handleFieldChange('recipientEmail', e.target.value, 'email');
                            }}
                            onFocus={(e) => handleFieldFocus('recipientEmail', 'email')}
                            onBlur={(e) => handleFieldBlur('recipientEmail')}
                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                              errors.recipientEmail
                                ? "border-red-500"
                                : "border-gray-300"
                            }`}
                            placeholder="jane@example.com"
                          />
                          {errors.recipientEmail && (
                            <p className="text-red-500 text-sm mt-1">
                              {errors.recipientEmail}
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      size="lg"
                      className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white text-lg py-6 h-auto mt-6">
                      Continue to Payment
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </form>
                </motion.div>
              )}

              {/* STEP 3: Payment */}
              {step === 3 && stripePromiseState && clientSecret && (
                <Elements
                  stripe={stripePromiseState}
                  options={{ clientSecret }}>
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}>
                    <PaymentStep
                      clientSecret={clientSecret}
                      formData={formData}
                      stripePromise={stripePromiseState}
                      onBack={handleBack}
                      onSuccess={(result) => {
                        handleClose();
                        if (onSuccess) onSuccess(result);
                      }}
                      variant={variant}
                      isForSomeoneElse={isForSomeoneElse}
                    />
                  </motion.div>
                </Elements>
              )}

              {step === 3 && loading && (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Preparing payment...</p>
                </div>
              )}
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}

/**
 * Payment Step Component
 */
function PaymentStep({
  clientSecret,
  formData,
  stripePromise,
  onBack,
  onSuccess,
  variant,
  isForSomeoneElse,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // Submit payment
      const { error: submitError } = await elements.submit();
      if (submitError) {
        throw new Error(submitError.message);
      }

      // Confirm payment
      const { error: confirmError, paymentIntent } =
        await stripe.confirmPayment({
          elements,
          clientSecret,
          confirmParams: {
            return_url: `${window.location.origin}/gift-cards/holiday/thank-you`,
            receipt_email: formData.email,
          },
          redirect: "if_required",
        });

      if (confirmError) {
        throw new Error(confirmError.message);
      }

      if (paymentIntent.status === "succeeded") {
        // Track successful payment
        await trackEvent("payment_complete", {
          amount: 89,
          currency: "CAD",
          paymentIntentId: paymentIntent.id,
          formData: {
            email: formData.email,
            fullName: formData.fullName,
          },
        });

        // Notify backend of successful payment with retry logic
        let retries = 0;
        const maxRetries = 3;
        let notificationSuccess = false;

        while (retries < maxRetries && !notificationSuccess) {
          try {
            const response = await fetch(
              `${BACKEND_BASE_URL}/api/gift-cards/holiday/payment-success`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  paymentIntentId: paymentIntent.id,
                  email: formData.email,
                  fullName: formData.fullName,
                  phone: formData.phone,
                  amount: 89,
                  variant,
                  recipientName: formData.recipientName || formData.fullName,
                  recipientEmail: formData.recipientEmail || formData.email,
                }),
              },
            );

            if (response.ok) {
              notificationSuccess = true;
              console.log("✅ Backend notified of payment success");
            } else {
              throw new Error(`HTTP ${response.status}`);
            }
          } catch (error) {
            retries++;
            if (retries < maxRetries) {
              console.warn(
                `Backend notification failed (attempt ${retries}/${maxRetries}), retrying...`,
                error,
              );
              await new Promise((resolve) =>
                setTimeout(resolve, 1000 * retries),
              );
            } else {
              console.error(
                "Failed to notify backend after payment. Thank you page will verify and process.",
                error,
              );
            }
          }
        }

        // Always redirect to thank you page
        if (onSuccess) {
          onSuccess({
            paymentIntentId: paymentIntent.id,
            email: formData.email,
            fullName: formData.fullName,
          });
        }
      }
    } catch (err) {
      setError(err.message);
      setProcessing(false);
    }
  };

  return (
    <div>
      <div className="text-center mb-6">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
          Complete Your Purchase
        </h2>
        <div className="bg-green-50 border-2 border-green-500 rounded-lg p-4 inline-block">
          <div className="text-3xl font-bold text-green-600">$89</div>
          <div className="text-sm text-gray-600">$120 Gift Card Value</div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <PaymentElement />

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        <div className="flex gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            className="flex-1">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button
            type="submit"
            disabled={!stripe || processing}
            className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700">
            {processing ? "Processing..." : `Pay $89`}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

        <p className="text-xs text-center text-gray-500">
          Secure checkout powered by Stripe
        </p>
      </form>
    </div>
  );
}
